import { ReactNode, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdMoreVert } from 'react-icons/md'
import useOnClickOutside from 'use-onclickoutside'
import { SidebarContext } from '../../../lib/Context'
import Button from '../../Formik/Button'
import CategoriesForm from '../Configuration/CategoriesForm'
import Configuration from '../Configuration/Configuration'
import Share from '../Configuration/Share'
import { AccountingType } from '../lib/Enums/AccountingType'
import { useAccounting } from '../lib/Hooks/Accounting'
import MenuButton from '../MenuButton'
import MenuLink from '../MenuLink'
import PaymentGuard from '../PaymentGuard'
import AccountingDevelopment from './AccountingDevelopment'
import AccountConfiguration from './Configuration'
import Expenses from './Expenses'
import ViewMode from './ViewMode'

const AccountingMenu = () => {
  const { t } = useTranslation(['accounting'])
  const [isActive, toggleActive] = useState(false)
  const { showSidebar } = useContext(SidebarContext)

  const { data: accounting } = useAccounting()
  const ref = useRef(null)

  const openSidebar = (content: ReactNode) => {
    showSidebar(content)
    toggleActive(false)
  }

  useOnClickOutside(ref, () => toggleActive(false))

  return (
    <PaymentGuard sheet={accounting!}>
      {(guard) => (
        <div className='flex flex-wrap md:justify-end mb-2 mt-2 sm:mt-0'>
          <div className='relative' ref={ref}>
            <Button
              theme='gray'
              className={`md:ml-2 cursor-pointer ${isActive ? 'rounded-b-none' : ''}`}
              onClick={() => toggleActive(!isActive)}
            >
              <MdMoreVert size='1.5rem' />
            </Button>
            {isActive && (
              <div className='absolute top-10 left-2 bg-white border-gray-300 border-2 px-4 pt-4 z-10'>
                <div className='sm:hidden'>
                  <MenuLink
                    title={t('accounting:menu.shareBudget')}
                    icon='MdShare'
                    action={() => openSidebar(<Share />)}
                  />
                </div>
                <div className='md:hidden'>
                  <MenuLink
                    title={t('accounting:menu.categories')}
                    icon='MdCategory'
                    action={() =>
                      openSidebar(<CategoriesForm sheet={accounting!} />)
                    }
                  />
                </div>
                <MenuLink
                  title={t('accounting:menu.expenses')}
                  icon='MdOutlineReceiptLong'
                  action={guard(() =>
                    openSidebar(
                      <Expenses
                        from={new Date(accounting!.created_date)}
                        to={new Date()}
                        unpaid={true}
                      />,
                    ),
                  )}
                />
                {accounting!.type !== AccountingType.REGULAR && (
                  <MenuLink
                    title={t('accounting:menu.development')}
                    icon='MdAutoGraph'
                    action={() =>
                      openSidebar(
                        <AccountingDevelopment accounting={accounting!} />,
                      )
                    }
                  />
                )}
              </div>
            )}
          </div>
          <ViewMode accounting={accounting!} />
          <div className='hidden sm:block'>
            <MenuButton
              sheet={accounting!}
              title={t('accounting:menu.shareAccounting')}
              icon='MdShare'
              onClick={() => showSidebar(<Share />)}
            ></MenuButton>
          </div>
          <div className='hidden md:block'>
            <MenuButton
              sheet={accounting!}
              title={t('accounting:menu.categories')}
              icon='MdCategory'
              onClick={() =>
                showSidebar(<CategoriesForm sheet={accounting!} />)
              }
            />
          </div>
          <MenuButton
            sheet={accounting!}
            icon='MdSettings'
            onClick={() =>
              showSidebar(
                <Configuration sheet={accounting!}>
                  <AccountConfiguration />
                </Configuration>,
              )
            }
          />
        </div>
      )}
    </PaymentGuard>
  )
}

export default AccountingMenu
